import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { profileUpdate } from "../../appStore/actions/profile/action";
import useAppSelectors from "../../customHooks/useAppSelectors";

const radioStyle = {
  color: "#092C4C",
  padding: "0.25rem",
  "&.Mui-checked": {
    color: "#092C4C",
  },
};

const DateTimeFormatDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { currentUserData } = useAppSelectors();

  const [selectedDateFormat, setSelectedDateFormat] = useState(
    currentUserData?.dateFormat || currentUserData?.date_format || "DD-MM-YY"
  );
  const [is24Hour, setIs24Hour] = useState(
    String(currentUserData?.timeFormat ?? currentUserData?.time_format ?? "12") === "24"
  );
  const [loading, setLoading] = useState(false);

  // Sync state when the dialog opens or profile data changes
  useEffect(() => {
    if (open) {
      setSelectedDateFormat(
        currentUserData?.dateFormat || currentUserData?.date_format || "DD-MM-YY"
      );
      setIs24Hour(
        String(currentUserData?.timeFormat ?? currentUserData?.time_format ?? "12") === "24"
      );
    }
  }, [open, currentUserData?.dateFormat, currentUserData?.date_format, currentUserData?.timeFormat, currentUserData?.time_format]);

  const handleDateFormatChange = (e) => {
    setSelectedDateFormat(e.target.value);
  };

  const handleTimeFormatChange = (e) => {
    setIs24Hour(e.target.checked);
  };

  const handleSave = async () => {
    setLoading(true);
    const reqBody = {
      date_format: selectedDateFormat,
      time_format: is24Hour ? "24" : "12",
    };
    try {
      const res = await dispatch(profileUpdate(reqBody));
      if (res?.status) {
        onClose();
      }
    } catch (err) {
      console.error("Failed to update date/time format", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          backgroundColor: "#F9F8F8",
        }}
      >
        <Typography fontSize={"1rem"} fontWeight={500} color="black">
          Your Date & Time Format
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: "#333" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: "16px 20px" }}>
        <Typography
          fontSize={"0.85rem"}
          fontWeight={500}
          color="black"
          mb={"1rem"}
        >
          Choose how dates and times appear for you. Other members will see
          dates in their own preferred format.
        </Typography>

        <Box display={"flex"} justifyContent={"space-between"}>
          <Box>
            <RadioGroup
              value={selectedDateFormat}
              onChange={handleDateFormatChange}
            >
              <FormControlLabel
                sx={{ marginBottom: "0.5rem" }}
                value="DD-MM-YY"
                control={<Radio sx={radioStyle} />}
                label={
                  <Typography fontSize={"0.87rem"} color="black">
                    DD-MM-YY
                  </Typography>
                }
                labelPlacement="end"
              />
              <FormControlLabel
                value="MM-DD-YY"
                control={<Radio sx={radioStyle} />}
                label={
                  <Typography fontSize={"0.87rem"} color="black">
                    MM-DD-YY
                  </Typography>
                }
                labelPlacement="end"
              />
            </RadioGroup>
          </Box>
          <Box>
            <RadioGroup
              value={selectedDateFormat}
              onChange={handleDateFormatChange}
            >
              <FormControlLabel
                sx={{ marginBottom: "0.5rem" }}
                value="YY-MM-DD"
                control={<Radio sx={radioStyle} />}
                label={
                  <Typography fontSize={"0.87rem"} color="black">
                    YY-MM-DD
                  </Typography>
                }
                labelPlacement="end"
              />
            </RadioGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={is24Hour}
                  onChange={handleTimeFormatChange}
                  sx={{
                    "& .MuiSvgIcon-root": { color: "#183243" },
                  }}
                />
              }
              label={
                <Typography fontSize={"0.87rem"} color="black">
                  24-Hour Time
                </Typography>
              }
              labelPlacement="end"
            />
          </Box>
        </Box>

        <Box display={"flex"} justifyContent={"center"} mt={"1.5rem"} mb={"0.5rem"}>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={loading}
            sx={{
              borderColor: "#092C4C",
              color: "#092C4C",
              textTransform: "none",
              width: "216px",
              borderRadius: "4px",
              "&:hover": {
                borderColor: "#092C4C",
                backgroundColor: "rgba(9, 44, 76, 0.04)",
              },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: "#092C4C" }} /> : "Save"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DateTimeFormatDialog;
